/* Copyright (c) 2020-present CloudIO Inc */

import { emptyFunction } from '@cloudio-saas/statex';
import { Platform } from '../../platform/Platform';
import {
  ComponentManager,
  ComponentManagerFn,
  OperatorProp,
  RequiredItem,
  ViewAttributeChooserProp,
} from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { baseComponentManager } from '../../registry/Components';
import { baseSearchFieldProps } from '../SearchTextField/SearchTextFieldManager';
import { baseFieldProps } from '../TextField/BaseField';
import { defaultValue } from './SearchDateField';

const SearchDateFieldManager: ComponentManagerFn<'SearchDateField'> = (
  platform: Platform,
  type: 'SearchDateField',
): ComponentManager<'SearchDateField'> => {
  let callback: () => void = emptyFunction;
  return {
    ...baseComponentManager<'SearchDateField'>(platform, 'SearchDateField'),
    type: 'SearchDateField',
    isChildAllowed: (page, item, childType) =>
      (['TriggerOnChange'] as ItemType[]).includes(childType),
    defaultValue,
    props: {
      ...baseSearchFieldProps,
      viewAttribute: {
        ...(baseFieldProps.viewAttribute as ViewAttributeChooserProp),
        getAttributeDataTypes: ({ props }: RequiredItem<'DateColumn'>) => {
          if (props.showTime) return ['DateTime'];
          return ['Date', 'DateTime'];
        },
        attributeRefreshCallback: (cb: () => void) => {
          callback = cb;
        },
      },
      defaultOperator: {
        ...(baseSearchFieldProps.defaultOperator as OperatorProp),
        getDataType: async () => {
          return 'Date';
        },
      },
      showTime: {
        label: 'Show Time',
        type: 'boolean',
      },
      format: {
        label: 'Format',
        type: 'string',
        helpText: 'e.g. MM/dd/yyyy hh:mm a',
      },
    },
    isPropertyVisible(prop, itemData, context): boolean {
      switch (prop) {
        case 'dataType':
        case 'updatable':
        case 'insertable': {
          return false;
        }
        default:
          return true;
      }
    },
    onPropertyChange: (property, value) => {
      if (property === 'showTime') {
        callback();
      }
      return {
        [property]: value,
      };
    },
    getDisplayText: (item) =>
      typeof item.props.label === 'string'
        ? item.props.label
        : item.props.label.value ?? '',
    name: 'Search Date Field',
    category: 'Search',
    icon: { iconName: 'calendar-edit' },
    description: `**Overview**<br/>
The Search Date Field is an input component designed for **Search Forms** that allows users to filter records based on specific date or time criteria. It provides an intuitive calendar picker and optional time selector to ensure users enter valid, queryable formats.
Use this component to enable searching for records created on a specific day, due before a certain deadline, or falling within a precise operational timeline.

**1. Data Compatibility**<br/>
The component binds to temporal fields in your Data Store to ensure search accuracy.
* **Date:** Used for standard calendar dates (e.g., "Filter by Birthdate").
* **DateTime:** Used for specific timestamps (e.g., "Filter by Transaction Time").
* **Smart Filtering:** The attribute picker automatically filters for valid fields based on your settings. For instance, if you enable **Show Time**, the component restricts your selection to **DateTime** fields only to prevent data mismatch.

**2. Configuration**<br/>
* **Show Time:** A boolean toggle that enables a clock interface alongside the calendar for granular time-based filtering.
* **Format:** Allows you to define how the selected date is displayed (e.g., "MM/dd/yyyy" or "dd/MM/yyyy HH:mm"). This ensures the user sees the date in a structure that matches local standards.
* **Default Operator:** Automatically sets search logic appropriate for chronological data, such as **"Equals"**, **"Greater Than"**, or **"Between"**.

**3. Interactivity**<br/>
* **TriggerOnChange:** Unlike some other search fields, this component supports the **TriggerOnChange** child.
* **Instant Filtering:** This allows you to trigger an immediate search refresh or update other UI elements the moment a user selects a date, providing a more responsive "live-filter" experience without requiring a separate "Submit" button.

**4. Behavior**<br/>
* **Read-Only Logic:** Properties like **Updatable** and **Insertable** are hidden because this component is strictly a query tool for finding existing data rather than creating or modifying records.
* **Standardization:** It inherits base search properties like **Label**, **Placeholder**, and **Help Text** for consistent form design.

**5. Best Use Cases**<br/>
* **Transaction Logs:** "Find all system error logs generated yesterday."
* **Order History:** "Show all customer orders placed between Jan 1st and Jan 31st."
* **Booking Systems:** "Search for available appointment slots on 10/12/2026."
* **Maintenance Scheduling:** "Filter equipment for all inspections due before the end of the current week."`,
  };
};

export default SearchDateFieldManager;
